/**
 * MPaisaReconciliationTab - M-PAiSA Payment Reconciliation Dashboard
 * Shows daily/weekly breakdown of QR Code vs Payment Link revenue
 * Includes pending payments management and auto-reconciliation
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  QrCode, Link2, Clock, CheckCircle, AlertCircle, RefreshCw,
  Calendar, Download, ChevronLeft, ChevronRight, TrendingUp,
  Smartphone, DollarSign, FileText, Timer
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function MPaisaReconciliationTab() {
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1); // Monday
    return d.toISOString().split('T')[0];
  });
  
  const [dailyReport, setDailyReport] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [pendingPayments, setPendingPayments] = useState(null);
  const [reminderStats, setReminderStats] = useState(null);

  const fetchDailyReport = useCallback(async () => {
    try {
      const res = await api.get(`/admin/mpaisa-reconciliation/daily?date=${selectedDate}`);
      if (res.data.success) {
        setDailyReport(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch daily report:', err);
    }
  }, [selectedDate]);

  const fetchWeeklyReport = useCallback(async () => {
    try {
      const res = await api.get(`/admin/mpaisa-reconciliation/weekly?week_start=${weekStart}`);
      if (res.data.success) {
        setWeeklyReport(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch weekly report:', err);
    }
  }, [weekStart]);

  const fetchPendingPayments = useCallback(async () => {
    try {
      const res = await api.get('/admin/mpaisa-reconciliation/pending');
      if (res.data.success) {
        setPendingPayments(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch pending payments:', err);
    }
  }, []);

  const fetchReminderStats = useCallback(async () => {
    try {
      const res = await api.get('/payments/mpaisa/reminder-stats');
      if (res.data.success) {
        setReminderStats(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch reminder stats:', err);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        viewMode === 'daily' ? fetchDailyReport() : fetchWeeklyReport(),
        fetchPendingPayments(),
        fetchReminderStats()
      ]);
      setLoading(false);
    };
    fetchData();
  }, [viewMode, fetchDailyReport, fetchWeeklyReport, fetchPendingPayments, fetchReminderStats]);

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      viewMode === 'daily' ? fetchDailyReport() : fetchWeeklyReport(),
      fetchPendingPayments(),
      fetchReminderStats()
    ]);
    setLoading(false);
    toast.success('Reconciliation data refreshed');
  };

  const navigateDate = (direction) => {
    if (viewMode === 'daily') {
      const d = new Date(selectedDate);
      d.setDate(d.getDate() + (direction === 'next' ? 1 : -1));
      setSelectedDate(d.toISOString().split('T')[0]);
    } else {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + (direction === 'next' ? 7 : -7));
      setWeekStart(d.toISOString().split('T')[0]);
    }
  };

  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { 
    weekday: 'short', month: 'short', day: 'numeric' 
  });

  const report = viewMode === 'daily' ? dailyReport : weeklyReport;
  const summary = report?.summary || {};
  const breakdown = report?.breakdown || {};

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Smartphone className="w-7 h-7 text-green-500" />
            M-PAiSA Reconciliation
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            QR Code vs Payment Link breakdown with pending payment tracking
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <Button
              variant={viewMode === 'daily' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('daily')}
              data-testid="recon-daily-btn"
            >
              Daily
            </Button>
            <Button
              variant={viewMode === 'weekly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('weekly')}
              data-testid="recon-weekly-btn"
            >
              Weekly
            </Button>
          </div>
          
          <Button variant="outline" onClick={handleRefresh} disabled={loading} data-testid="recon-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="flex items-center justify-center gap-4 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
        <Button variant="ghost" size="sm" onClick={() => navigateDate('prev')}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2 text-lg font-medium text-gray-900 dark:text-white">
          <Calendar className="w-5 h-5 text-green-500" />
          {viewMode === 'daily' ? (
            formatDate(selectedDate)
          ) : (
            <span>
              Week of {formatDate(weekStart)} - {formatDate(new Date(new Date(weekStart).getTime() + 6 * 24 * 60 * 60 * 1000).toISOString())}
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigateDate('next')}>
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total M-PAiSA Revenue */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-green-100 text-sm font-medium">Total M-PAiSA</span>
            <DollarSign className="w-5 h-5 text-green-200" />
          </div>
          <div className="text-3xl font-bold">{formatCurrency(summary.total_mpaisa_revenue)}</div>
          <div className="text-green-100 text-sm mt-1">{summary.total_transactions || 0} transactions</div>
        </div>

        {/* QR Code Revenue */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">QR Code (Static)</span>
            <QrCode className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(summary.qr_code_revenue || summary.qr_code?.revenue)}</div>
          <div className="text-blue-600 dark:text-blue-400 text-sm mt-1">
            {viewMode === 'weekly' && summary.qr_code?.percentage ? `${summary.qr_code.percentage}%` : ''} 
            {breakdown.qr_code?.order_count || summary.qr_code?.transactions || 0} orders
          </div>
        </div>

        {/* Payment Link Revenue */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-purple-200 dark:border-purple-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Payment Links</span>
            <Link2 className="w-5 h-5 text-purple-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(summary.payment_link_revenue || summary.payment_link?.revenue)}</div>
          <div className="text-purple-600 dark:text-purple-400 text-sm mt-1">
            {viewMode === 'weekly' && summary.payment_link?.percentage ? `${summary.payment_link.percentage}%` : ''} 
            {breakdown.payment_link?.order_count || summary.payment_link?.transactions || 0} orders
          </div>
        </div>

        {/* Pending Payments */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-amber-200 dark:border-amber-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Pending</span>
            <Clock className="w-5 h-5 text-amber-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{pendingPayments?.pending_count || 0}</div>
          <div className="text-amber-600 dark:text-amber-400 text-sm mt-1">
            {pendingPayments?.auto_reconciled_count || 0} auto-reconciled
          </div>
        </div>
      </div>

      {/* Reminder Stats (Today) */}
      {reminderStats?.stats && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Timer className="w-5 h-5 text-blue-500" />
            Payment Reminder Stats (Today)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{reminderStats.stats.total_payments_created}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Created</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{reminderStats.stats.reminders_sent}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Reminders Sent</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{reminderStats.stats.extensions_granted}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Extensions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{reminderStats.stats.completed}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{reminderStats.stats.completion_rate}%</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Completion Rate</div>
            </div>
          </div>
        </div>
      )}

      {/* Reconciliation Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* QR Code Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <QrCode className="w-5 h-5 text-blue-500" />
              QR Code Payments
            </h3>
            {breakdown.qr_code?.match_status && (
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                breakdown.qr_code.match_status === 'matched' 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }`}>
                {breakdown.qr_code.match_status === 'matched' ? (
                  <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Matched</span>
                ) : (
                  <span className="flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Mismatch</span>
                )}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Customer scans cafe QR code, cashier enters reference
          </p>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Orders Total</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(breakdown.qr_code?.order_total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Transactions Total</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(breakdown.qr_code?.transaction_total)}</span>
            </div>
            {breakdown.qr_code?.variance !== 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Variance</span>
                <span className={`font-medium ${breakdown.qr_code?.variance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {breakdown.qr_code?.variance > 0 ? '+' : ''}{formatCurrency(breakdown.qr_code?.variance)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Payment Link Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Link2 className="w-5 h-5 text-purple-500" />
              Payment Link Orders
            </h3>
            {breakdown.payment_link?.match_status && (
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                breakdown.payment_link.match_status === 'matched' 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }`}>
                {breakdown.payment_link.match_status === 'matched' ? (
                  <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Matched</span>
                ) : (
                  <span className="flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Mismatch</span>
                )}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Online, Vapi, WhatsApp, Kiosk payment links
          </p>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Orders Total</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(breakdown.payment_link?.order_total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Transactions Total</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(breakdown.payment_link?.transaction_total)}</span>
            </div>
            {breakdown.payment_link?.pending_payments > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Pending in Gateway</span>
                <span className="font-medium text-amber-600">{breakdown.payment_link?.pending_payments}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Weekly Breakdown Table (if weekly view) */}
      {viewMode === 'weekly' && weeklyReport?.daily_breakdown?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Daily Breakdown
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">QR Code</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Payment Link</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {weeklyReport.daily_breakdown.map((day, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatDate(day.date)}</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-600 dark:text-blue-400">
                      {formatCurrency(day.qr_code.total)} ({day.qr_code.count})
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-purple-600 dark:text-purple-400">
                      {formatCurrency(day.payment_link.total)} ({day.payment_link.count})
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                      {formatCurrency(day.daily_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending Payments List */}
      {pendingPayments?.pending_payments?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Pending Payments ({pendingPayments.pending_count})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Source</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Age</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pendingPayments.pending_payments.slice(0, 10).map((payment, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                      {payment.order_id?.slice(0, 8)}...
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        payment.source === 'vapi' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                        payment.source === 'whatsapp' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        payment.source === 'kiosk' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {payment.source || 'web'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-500 dark:text-gray-400">
                      {payment.age_minutes}m ago
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Status Badge */}
      {summary.reconciliation_status && (
        <div className="flex justify-center">
          <div className={`px-4 py-2 rounded-full text-sm font-medium ${
            summary.reconciliation_status === 'clean' || summary.reconciliation_status === 'no_activity'
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
          }`}>
            {summary.reconciliation_status === 'clean' ? (
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                All Reconciled - No Discrepancies
              </span>
            ) : summary.reconciliation_status === 'no_activity' ? (
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                No M-PAiSA Activity
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Needs Review
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
